import React from "react";
import { useState } from 'react';
import { LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useAdAccount } from '@/contexts/AdAccountContext';

export function UserMenu() {
  const { user, signOut } = useAuth();
  const { selectedAdAccountName } = useAdAccount();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const email = user?.email || '';
  const initials = email ? email[0].toUpperCase() : 'U';

  const tooltipLabel = selectedAdAccountName
    ? `${email} — ${selectedAdAccountName}`
    : email;

  const handleLogout = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } catch {
      // Always proceed with cleanup
    }
    window.location.href = '/auth';
  };

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Avatar className="h-9 w-9 cursor-pointer">
                  <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[240px]">
            <p className="truncate text-xs">{tooltipLabel}</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent side="right" align="end" className="w-56">
          <DropdownMenuLabel className="font-normal space-y-0.5">
            <p className="text-sm truncate text-muted-foreground">{email}</p>
            {selectedAdAccountName && (
              <p className="text-xs truncate text-muted-foreground/70">{selectedAdAccountName}</p>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive cursor-pointer"
            disabled={isSigningOut}
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {isSigningOut ? 'Signing out...' : 'Log out'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
